import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { GEREKCE_AGIR_EN_AZ } from "@koclukpanel/contracts";
import { Alan, BosDurum, Buton, Iskelet, Kart, NotKutusu, Rozet } from "@koclukpanel/ui";
import {
  ApiHatasi,
  mesajDenetimOku,
  mesajDenetimYazismalari,
  type DenetimYazismasi,
  type MesajKanali,
} from "./api";
import { ekBoyutu } from "./Mesajlar";
import { useSudoKapisi, useSudoOnce } from "./SudoKapisi";

/**
 * GEREKÇELİ YAZIŞMA OKUMA — F-MSG-06
 *
 * ================================================================
 * AYRI DOSYA, AYRI İZİN
 * ================================================================
 * `Mesajlar.tsx` kişinin KENDİ yazışmaları; burası BAŞKASININ.
 * İkisini tek bileşene gömseydim, yalnızca birini taşıyan kullanıcı
 * için ekranın yarısı sessizce boş açılırdı — bu depoda ölçülmüş bir
 * hata sınıfı.
 *
 * ================================================================
 * ÜÇ KAPI ÜST ÜSTE
 * ================================================================
 *   1. `mesaj.icerik.oku` izni — özel role EKLENEMEZ (`isRestricted`)
 *   2. Sudo — şifre yeniden doğrulanıyor (izin sudo listesinde;
 *      dekoratörle değil işin kendisiyle geliyor, CLAUDE.md kural 10)
 *   3. Gerekçe — en az {GEREKCE_AGIR_EN_AZ} karakter, denetim kaydına yazılıyor
 *
 * ================================================================
 * GİZLİ DENETİM YAPILMIYOR
 * ================================================================
 * Şartnamenin kendi cümlesi. Okuma denetim kaydına geçiyor ve bu
 * durumun mümkün olduğu **kullanıcıya gösterilen ekranda** da yazılı.
 * Sessizce okunabilen bir yazışma "gerekçeli erişim" değil, yalnızca
 * gecikmeli erişimdir.
 *
 * Emre'nin kararı (06.09.2026): teknik yönetici bu izne sahip.
 * Sahip olmak izsiz kullanmak demek değil — kayıt, yetkinin karşıtı
 * değil kullanımının izidir.
 */

const KANAL_ADI: Record<MesajKanali, string> = {
  KOC: "Koçluk",
  KURUM: "Kurum",
  PERSONEL: "Personel",
};

function zaman(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" });
}

function OkumaPaneli({ yazisma }: { yazisma: DenetimYazismasi }) {
  const [gerekce, setGerekce] = useState("");
  const { hataYakala, kapi } = useSudoKapisi(
    "Başkasının yazışmasını okumak için şifrenizi yeniden girin.",
  );

  const oku = useMutation({
    mutationFn: () => mesajDenetimOku(yazisma.id, gerekce),
    onError: hataYakala(() => oku.mutate()),
  });

  return (
    <div className="bg-notr-50 rounded-alan mt-2 flex flex-col gap-3 p-3">
      {kapi}
      <Alan
        etiket="Bu yazışmayı neden okuyorsunuz?"
        yardim={`Denetim kaydına yazılır, en az ${GEREKCE_AGIR_EN_AZ} karakter. Gerekçe, okumanın kendisinden daha uzun ömürlü: altı ay sonra "bu yazışmaya neden bakıldı" sorusunu cevaplayacak tek şey.`}
        value={gerekce}
        onChange={(e) => setGerekce(e.target.value)}
      />
      <div>
        <Buton
          onClick={() => oku.mutate()}
          yukleniyor={oku.isPending}
          yukleniyorMetni="Okunuyor"
          /*
            EŞİK SÖZLEŞMEDEN. Ekran kendi sayısını yazsaydı, sunucununkiyle
            ayrıştığı gün ya kabul edilecek bir gerekçeyi engellerdi ya da
            reddedilecek birini gönderirdi (`golgeleme.test.ts`).
          */
          disabled={gerekce.trim().length < GEREKCE_AGIR_EN_AZ}
        >
          Yazışmayı aç
        </Buton>
      </div>

      {oku.isError && (
        <p role="alert" className="text-hata-700 text-sm">
          {oku.error instanceof ApiHatasi ? oku.error.message : "Yazışma okunamadı."}
        </p>
      )}

      {oku.isSuccess && (
        <div role="status" className="flex flex-col gap-3">
          <p className="text-notr-700 text-sm">
            Bu okuma denetim kaydına geçti: {oku.data.katilimcilar.join(", ")} ·{" "}
            {oku.data.mesajlar.length} mesaj.
          </p>
          <ul className="border-notr-200 bg-notr-0 rounded-alan max-h-96 overflow-y-auto border">
            {oku.data.mesajlar.map((m) => (
              <li key={m.id} className="border-notr-200 border-b p-3 last:border-b-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-notr-900 text-sm font-medium">{m.gonderenAd}</span>
                  <span className="text-notr-600 text-xs tabular-nums">{zaman(m.createdAt)}</span>
                </div>
                {m.ek && (
                  /*
                    EKİN ADI DENETİMDE GÖRÜNÜYOR — geri alınmış olsa
                    bile. Bir taciz iddiasında incelenecek şey çoğu
                    zaman metin değil gönderilen görseldir; ekin
                    varlığını denetimden gizlemek F-MSG-06'yı asıl
                    gerektiği yerde işlevsiz bırakırdı.

                    İNDİRME DÜĞMESİ YOK: indirme yazışmanın kendi
                    izniyle çalışıyor (`mesaj/:id/ek`) ve denetçi o
                    yazışmanın katılımcısı değil. Buraya bir indirme
                    yolu açmak, gerekçeli okumayı sessiz bir dosya
                    erişimine çevirirdi.
                  */
                  <Rozet ton="notr">
                    📎 {m.ek.ad} · {ekBoyutu(m.ek.boyut)}
                  </Rozet>
                )}
                {m.geriAlindiMi && (
                  /*
                    GERİ ALINAN MESAJIN GÖVDESİ BURADA GÖRÜNÜYOR ve
                    F-MSG-01b'nin can alıcı noktası bu: geri alma ekrandan
                    gizler, denetimden gizlemez. Aksi hâlde geri alma, bir
                    şikâyet durumunda kanıtı yok etmenin meşru yolu olurdu.
                    İşaret duruyor ki okuyan, karşı tarafın bunu geri
                    çektiğini bilsin.
                  */
                  <Rozet ton="uyari">Gönderen geri aldı — ekranda görünmüyor</Rozet>
                )}
                <p className="text-notr-900 mt-1 text-sm break-words whitespace-pre-wrap">
                  {m.govde}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Satir({ yazisma }: { yazisma: DenetimYazismasi }) {
  const [acik, setAcik] = useState(false);

  return (
    <li className="border-notr-200 border-b py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-notr-900 text-sm">
            {yazisma.katilimcilar.join(" ↔ ") || "Katılımcı yok"}
          </p>
          <p className="text-notr-600 mt-1 text-xs">
            {yazisma.mesajSayisi} mesaj · son mesaj {zaman(yazisma.sonMesajAt)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Rozet ton="notr">{KANAL_ADI[yazisma.kanal]}</Rozet>
          {yazisma.arsivlendiMi && <Rozet ton="notr">Arşiv</Rozet>}
          {yazisma.geriAlinan > 0 && <Rozet ton="uyari">{yazisma.geriAlinan} geri alınmış</Rozet>}
          <Buton gorunum="ikincil" onClick={() => setAcik((o) => !o)}>
            {acik ? "Vazgeç" : "Gerekçeyle oku"}
          </Buton>
        </div>
      </div>
      {acik && <OkumaPaneli yazisma={yazisma} />}
    </li>
  );
}

export function MesajDenetim() {
  const [ara, setAra] = useState("");
  const [sorgu, setSorgu] = useState("");

  // Pencere kapaliysa sifre ONCE sorulur, liste sorgusu 403 atmaz (UI/UX Dalga 2, M6).
  const { sudoHazir, onKapi } = useSudoOnce(
    "Yazışma denetimi ekranı başkalarının yazışmalarını listeler. Devam etmek için şifrenizi yeniden girin.",
  );

  const liste = useQuery({
    queryKey: ["mesaj-denetim-liste", sorgu],
    queryFn: () => mesajDenetimYazismalari(sorgu),
    retry: false,
    enabled: sudoHazir,
  });

  /*
    ================================================================
    LİSTENİN KENDİSİ DE SUDO İSTİYOR — VE BURASI SORMUYORDU
    ================================================================
    `mesaj.icerik.oku` sudo listesinde ve bu ekranın LİSTE ucu da o
    izni soruyor. Kanca aşağıda `OkumaPaneli` içinde kuruluydu: bir
    yazışma AÇILIRKEN şifre isteniyordu. Ama o panele ulaşmak için
    önce listenin gelmesi gerekiyor ve liste 403 dönüyordu.

    07.09.2026'da canlı ölçüldü: SUPER_ADMIN ekranı açıyor,
    `GET /mesaj/denetim/yazismalar` 403 SUDO_GEREKLI dönüyor, ekranda
    "Liste alınamadı" ve bir "Tekrar dene" düğmesi çıkıyor. O düğme
    sonsuza kadar aynı 403'ü alır — kullanıcıya ne yapması gerektiği
    söylenmiyor, yapacak yer hiç verilmiyor.

    `Entegrasyonlar` ve `Roller` ekranlarında 06.08.2026'da bulunan
    hatanın aynısı. Mekanizma (`sorguKapisi`) o gün tam bunun için
    yazılmıştı; bu ekran onu kullanmıyordu.
  */
  const sayfaKapisi = useSudoKapisi(
    "Yazışma denetimi ekranı başkalarının yazışmalarını listeler. Devam etmek için şifrenizi yeniden girin.",
  );

  if (onKapi) return <>{onKapi}</>;
  if (liste.isError) {
    const kapi = sayfaKapisi.sorguKapisi(liste.error, () => void liste.refetch());
    if (kapi) return <>{kapi}</>;
  }

  return (
    <div className="flex flex-col gap-4">
      <NotKutusu ton="uyari">
        <strong>Bu ekran başkalarının yazışmalarını açar.</strong> Her okuma, gerekçesiyle birlikte
        denetim kaydına yazılır ve silinemez. Gizli denetim yapılmaz: kullanıcılara bu durumun
        mümkün olduğu kendi ekranlarında da bildirilir. Geri alınmış mesajların gövdesi burada
        görünür — geri alma ekrandan gizler, denetimden gizlemez.
      </NotKutusu>

      <Kart
        baslik="Yazışmalar"
        aciklama="Aşağıda yalnızca üst veri var: kim, hangi kanal, kaç mesaj. İçeriği görmek ayrı bir adım ve gerekçe istiyor."
      >
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setSorgu(ara);
          }}
        >
          <div className="min-w-56 flex-1">
            <Alan
              etiket="Kişi adına göre ara"
              yardim="Katılımcının adı ya da soyadı. Boş bırakılırsa en son yazılan 50 yazışma listelenir."
              value={ara}
              onChange={(e) => setAra(e.target.value)}
            />
          </div>
          <Buton type="submit">Ara</Buton>
        </form>

        <div className="mt-3">
          {liste.isPending ? (
            <Iskelet satir={4} />
          ) : liste.isError ? (
            <BosDurum
              ton="hata"
              baslik="Liste alınamadı"
              aciklama={liste.error instanceof ApiHatasi ? liste.error.message : undefined}
              eylem={
                <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
                  Tekrar dene
                </Buton>
              }
            />
          ) : liste.data.yazismalar.length === 0 ? (
            <p className="text-notr-700 text-sm">
              {sorgu ? "Bu aramaya uyan yazışma yok." : "Sistemde henüz yazışma yok."}
            </p>
          ) : (
            <ul className="flex flex-col">
              {liste.data.yazismalar.map((y) => (
                <Satir key={y.id} yazisma={y} />
              ))}
            </ul>
          )}
        </div>
      </Kart>
    </div>
  );
}
